import type Comment                     from '@/core/services/Comments/Comment';
import type Person                           from "@/domains/Persons/Person";
import Title_ru_RU, { FieldDescriptor } from "@/core/misc/decorators/Title_ru_RU";
import serializeSimplified              from "@/core/misc/decorators/SerializeSimplified";
import TYPES                            from '@/core/services/Changelogs/types';
import Core                             from "@/core/Core";
import Serializable                     from "@/core/misc/mixins/Serializable";
import TimeStamp                        from "@/core/types/TimeStamp";
import CommentsStatus                   from '@/core/services/Comments/CommentsStatus';

class BaseEntity extends Serializable {
	/**
	 * Человекочитаемое имя типа сущности (например, «Партнёр», «Тендер»).
	 * Заполняется декоратором @Title_ru_RU_Class на уровне класса-наследника.
	 * Доступ снаружи — через геттер entityTypeName на инстансе.
	 */
	private static __entityTypeName?: string;

	id: number;
	@Title_ru_RU('версия', TYPES.NUMBER)
	version: number;
	@Title_ru_RU('отметка удаления', TYPES.BOOLEAN)
	isDeleted: boolean;
	@Title_ru_RU('дата создания', TYPES.DATE)
	createdAt: TimeStamp | undefined;
	@Title_ru_RU('кем создана', TYPES.OBJECT)
	@serializeSimplified
	createdBy: Person | null;
	/** Дата обновления */
	@Title_ru_RU('когда обновлено', TYPES.DATE)
	updatedAt: TimeStamp | null;

	/**
	 * Метаинформация о комментариях. null когда бэк её не подмешал
	 * (например реестр запросили без requireCommentsInfo).
	 * Для некомментируемых сущностей остаётся null навсегда — это честно.
	 */
	commentsStatus: CommentsStatus | null = null;

	/**
	 * Локальный буфер комментов для ещё-не-сохранённой сущности.
	 * Уйдёт на бэк вместе с сущностью при создании, развернётся в реальные
	 * комменты через CommentsService::addComments($entity).
	 *
	 * Тип Comment взят как type-only чтобы не создавать цикл импортов
	 * BaseEntity → Comment → BaseEntity.
	 */
	commentaries: Comment[] = [];

	/** Сущность сохранена на сервере (имеет id) */
	get isExisting(): boolean {
		return Core.utils.number.isNumber(this.id);
	}

	/** Форматированный номер с разделителями */
	get formattedNumber(): string {
		return this.id ? Core.utils.string.formatWithDelimiter(this.id) : '';
	}

	toString(): string {
		const name = (this as { name?: unknown }).name;

		if (typeof name === 'string' && name.trim() !== '') {
			// Именованная сущность (Партнёр, Производитель, Торговая марка) — выводим имя
			return name;
		} else if ('name' in this) {
			// Именуемая сущность, но имя не заполнено — выводим тип + номер
			const type   = (this.constructor as any).__entityTypeName;
			const number = this.formattedNumber ? `№${this.formattedNumber}` : '';

			return `${type} ${number}`.trim();
		} else {
			// Документ без имени как такового (Заказ, Отгрузка, Сборка) — тип + номер + дата
			const type   = (this.constructor as any).__entityTypeName;
			const number = this.formattedNumber ? `№${this.formattedNumber}` : '';
			const date   = this.createdAt ? `от ${this.createdAt.format()}` : '';

			return `${type} ${number} ${date}`.trim();
		}
	}

    /**Сбрасывает идентификаторы сущности — превращает в «новую» для бэка.*/
    // Это нужно как минимум для создания копии заказа
	resetIdentity(): void {
		(this as any).id = null;
		(this as any).version = null;
		(this as any).commentsStatus = null;
		(this as any).commentaries = [];
	}

    /**
     * Гарантирует наличие commentsStatus и возвращает его.
     * Нужен для случая когда сущность приехала без статуса (бэк не подмешал),
     * но мы оптимистично добавили коммент и хотим обновить локальный счётчик.
     */
    ensureCommentsStatus(): CommentsStatus {
        if (this.commentsStatus === null) {
            this.commentsStatus = new CommentsStatus();
        }
        return this.commentsStatus;
    }

    static fromServer(baseEntityData, result = new BaseEntity()): BaseEntity {
        super.fromServer(baseEntityData, result);

		result.createdAt = Core.utils.hydrate(baseEntityData.createdAt, TimeStamp.fromServer)!;
		//result.createdBy = Core.utils.hydrate(baseEntityData.createdBy, Person.fromServer);
		result.updatedAt = Core.utils.hydrate(baseEntityData.updatedAt, TimeStamp.fromServer);

        if (baseEntityData.commentsStatus) {
			result.commentsStatus = CommentsStatus.fromServer(baseEntityData.commentsStatus);
        }

        return result;
    }

	/**
	 * Сущность из свободного ввода VSuggestionSelectV2: пользователь набрал имя,
	 * в списке ничего не нашлось, наверх должна уйти новая сущность.
	 *
	 * Пишем в name — то же поле, которое читает toString(), поэтому введённое
	 * и отображённое совпадают. Переопределяйте в модели, если новой сущности
	 * нужно заполнить не только имя (например страну поставщика) или если имя
	 * лежит не в name.
	 */
	static fromSuggestion<T extends BaseEntity>(this: new () => T, name: string): T {
		const entity = new this();

		(entity as { name?: unknown }).name = name;

		return entity;
	}

	/** ------------- Этот метод позволит ide подсказать все декорируемые поля, если верить клоду ----------------- */
	/** Метод работающий с декораторами
	 * Метод для получения дескрипторов с учётом наследования
	 * **/
	static getFieldDescriptors<T extends typeof BaseEntity>(this: T): Record<keyof InstanceType<T>, FieldDescriptor> {
		const descriptors: Record<string, FieldDescriptor> = {};
		let currentClass: any = this;

		// Идём по цепочке прототипов
		while (currentClass && currentClass._fieldDescriptors) {
			Object.assign(descriptors, currentClass._fieldDescriptors);
			currentClass = Object.getPrototypeOf(currentClass);
		}

		return descriptors as Record<keyof InstanceType<T>, FieldDescriptor>;
	}

	/**
	 * Статический доступ — `Partner.entityTypeName`.
	 */
	static get entityTypeName(): string | undefined {
			return this.__entityTypeName;
	}
  get entityTypeName(): string | undefined {
    return (this.constructor as typeof BaseEntity).entityTypeName;
  }
}

export default BaseEntity;